import { Dispatch, SetStateAction, useCallback, useEffect } from "react";
import { scopedTemplateIdFor } from "./policyFilterOptions";

interface TemplateScopedFilters {
    templateIds: string[];
}

interface UseUrlScopedTemplateIdsParams<
    TFilters extends TemplateScopedFilters,
> {
    templateId: string | null;
    onTemplateIdChange: (templateId: string | null) => void;
    setFilters: Dispatch<SetStateAction<TFilters>>;
}

/**
 * Keeps a tab's Policy filter and the `templateId` URL param in step. A URL
 * scope wins over the local filter; an absent scope means "unscoped or
 * several policies", so it leaves the local selection alone.
 */
export function useUrlScopedTemplateIds<
    TFilters extends TemplateScopedFilters,
>({
    templateId,
    onTemplateIdChange,
    setFilters,
}: UseUrlScopedTemplateIdsParams<TFilters>): (
    nextTemplateIds: string[],
) => void {
    useEffect(() => {
        if (!templateId) {
            return;
        }
        setFilters((current) =>
            current.templateIds.length === 1 &&
            current.templateIds[0] === templateId
                ? current
                : { ...current, templateIds: [templateId] },
        );
    }, [setFilters, templateId]);

    return useCallback(
        (nextTemplateIds: string[]) => {
            const nextTemplateId = scopedTemplateIdFor(nextTemplateIds);
            if (nextTemplateId !== templateId) {
                onTemplateIdChange(nextTemplateId);
            }
        },
        [onTemplateIdChange, templateId],
    );
}
